/**
 * Jira Integration Service
 * Connects to PHP backend at /api/jira.php
 */

const JIRA_API = '/api/jira.php';

const getAuthToken = (): string | null => localStorage.getItem('session_token');

interface JiraConfig {
  jira_url: string;
  jira_email: string;
  jira_default_project: string;
  is_configured: boolean;
}

interface JiraProject {
  key: string;
  name: string;
  id: string;
}

interface JiraIssue {
  key: string;
  id: string;
  summary: string;
  status: string;
  status_color?: string;
  priority: string;
  issue_type: string;
  assignee: string | null;
  reporter: string | null;
  created: string;
  updated: string;
  labels: string[];
}

interface JiraIssueType {
  id: string;
  name: string;
  description?: string;
  subtask?: boolean;
}

interface JiraPriority {
  id: string;
  name: string;
  iconUrl?: string;
}

interface JiraLink {
  id: number;
  entity_type: string;
  entity_id: number;
  jira_issue_key: string;
  jira_issue_url: string;
  jira_issue_summary: string;
  link_type: string;
  linked_by: number;
  created_at: string;
  first_name?: string;
  last_name?: string;
  linked_by_email?: string;
}

interface CreateTaskParams {
  project_key?: string;
  summary: string;
  description?: string;
  issue_type_id?: string;
  priority_id?: string;
  labels?: string[];
  entity_type?: string;
  entity_id?: number;
}

interface LinkIssueParams {
  entity_type: string;
  entity_id: number;
  jira_issue_key: string;
  link_type?: 'evidence' | 'task' | 'related';
}

async function jiraApi<T>(action: string, options?: {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: any;
  params?: Record<string, string | number>;
}): Promise<{ success: boolean; data?: T; error?: string }> {
  const { method = 'GET', body, params = {} } = options || {};
  
  const url = new URL(window.location.origin + JIRA_API);
  url.searchParams.set('action', action);
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, String(v)));
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  const token = getAuthToken();
  if (token) {
    headers['X-Session-Token'] = token;
  }
  
  try {
    const res = await fetch(url.toString(), {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    
    const json = await res.json();
    
    if (!res.ok || json.error) {
      return { success: false, error: json.error || `HTTP ${res.status}` };
    }
    
    return { success: true, data: json as T };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Network error' };
  }
}

export const jiraService = {
  // ─── Configuration ─────────────────────────────────
  
  async getConfig(): Promise<JiraConfig | null> {
    const res = await jiraApi<{ config: JiraConfig }>('config');
    return res.data?.config || null;
  },
  
  async saveConfig(config: {
    jira_url: string;
    jira_email: string;
    jira_api_token?: string;
    jira_default_project?: string;
  }): Promise<{ success: boolean; error?: string }> {
    return jiraApi('config', { method: 'POST', body: config });
  },
  
  async testConnection(): Promise<{ success: boolean; user?: { display_name: string; email: string }; error?: string }> {
    const res = await jiraApi<{ user: { display_name: string; email: string; account_id: string } }>('test-connection', { method: 'POST' });
    if (res.success && res.data) {
      return { success: true, user: res.data.user };
    }
    return { success: false, error: res.error };
  },
  
  async disconnect(): Promise<{ success: boolean; error?: string }> {
    return jiraApi('disconnect', { method: 'POST' });
  },
  
  async getStatus(): Promise<{ connected: boolean; jira_url?: string }> {
    const res = await jiraApi<{ connected: boolean; jira_url: string | null }>('status');
    return {
      connected: res.data?.connected || false,
      jira_url: res.data?.jira_url || undefined,
    };
  },
  
  // ─── Projects & Metadata ───────────────────────────
  
  async getProjects(): Promise<JiraProject[]> {
    const res = await jiraApi<{ projects: JiraProject[] }>('projects');
    return res.data?.projects || [];
  },
  
  async getIssueTypes(projectKey: string): Promise<JiraIssueType[]> {
    const res = await jiraApi<{ issue_types: JiraIssueType[] }>('issue-types', {
      params: { project: projectKey }
    });
    return res.data?.issue_types || [];
  },
  
  async getPriorities(): Promise<JiraPriority[]> {
    const res = await jiraApi<{ priorities: JiraPriority[] }>('priorities');
    return res.data?.priorities || [];
  },
  
  // ─── Search & Fetch Issues ─────────────────────────
  
  async searchIssues(options: {
    jql?: string;
    project?: string;
    text?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ issues: JiraIssue[]; total: number }> {
    const res = await jiraApi<{ issues: JiraIssue[]; total: number; offset: number; limit: number }>('search', {
      params: {
        ...(options.jql && { jql: options.jql }),
        ...(options.project && { project: options.project }),
        ...(options.text && { text: options.text }),
        ...(options.limit && { limit: options.limit }),
        ...(options.offset && { offset: options.offset }),
      }
    });
    return {
      issues: res.data?.issues || [],
      total: res.data?.total || 0,
    };
  },
  
  async getIssue(issueKey: string): Promise<JiraIssue | null> {
    const res = await jiraApi<{ issue: JiraIssue }>('issue', {
      params: { key: issueKey }
    });
    return res.data?.issue || null;
  },
  
  // ─── Create Tasks ──────────────────────────────────
  
  async createTask(params: CreateTaskParams): Promise<{ 
    success: boolean; 
    issue?: { key: string; id: string; url: string }; 
    error?: string 
  }> {
    const res = await jiraApi<{ issue: { key: string; id: string; url: string } }>('create-task', {
      method: 'POST',
      body: params,
    });
    
    if (res.success && res.data?.issue) {
      return { success: true, issue: res.data.issue };
    }
    return { success: false, error: res.error };
  },
  
  // ─── Link Management ───────────────────────────────
  
  async linkIssue(params: LinkIssueParams): Promise<{ success: boolean; link?: JiraLink; error?: string }> {
    const res = await jiraApi<{ link: JiraLink }>('link', {
      method: 'POST',
      body: params,
    });
    
    if (res.success && res.data?.link) {
      return { success: true, link: res.data.link };
    }
    return { success: false, error: res.error };
  },
  
  async unlinkIssue(linkId: number): Promise<{ success: boolean; error?: string }> {
    return jiraApi('link', {
      method: 'DELETE',
      params: { id: linkId },
    });
  },
  
  async getEntityLinks(entityType: string, entityId: number): Promise<JiraLink[]> {
    const res = await jiraApi<{ links: JiraLink[] }>('links', {
      params: { entity_type: entityType, entity_id: entityId }
    });
    return res.data?.links || [];
  },
};

export type { 
  JiraConfig, JiraProject, JiraIssue, JiraIssueType, 
  JiraPriority, JiraLink, CreateTaskParams, LinkIssueParams 
};
